/* Product rails that load as the shopper scrolls (sections/product-rail.liquid, Loading: "As the shopper scrolls").
   Such a rail arrives as a shell, <lmb-product-rail data-section-id>: its title, its "See all" links and empty cells
   the size of real cards, so nothing moves when the cards come in. About one screen before a shell scrolls into
   view it joins a short queue; the queue is sent as one Section Rendering API request per 5 rails (Shopify's limit):
   location.pathname + '?sections=<id>,<id>…', which answers JSON with each rail's full HTML. The shell's empty track
   is then swapped for the real one. Nothing is added to the address bar or the history.
   If a request fails, the shell stays with its title and "See all" links (the empty cells go), so the collection is
   still one tap away. Quick add in the new cards is handled by the global assets/quick-add.js. */
(() => {
  if (customElements.get('lmb-product-rail')) return;

  const MAX_PER_REQUEST = 5;
  /* Rails that reach the screen within this many ms of each other share one request. */
  const QUEUE_MS = 40;
  const queue = [];
  let queueTimer = null;
  let observer = null;

  async function load(rails) {
    const ids = rails.map((rail) => encodeURIComponent(rail.dataset.sectionId)).join(',');
    let sections = null;
    try {
      const response = await fetch(`${window.location.pathname}?sections=${ids}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (response.ok) sections = await response.json();
    } catch (error) {
      sections = null;
    }
    rails.forEach((rail) => rail.fill(sections ? sections[rail.dataset.sectionId] : null));
  }

  function flush() {
    queueTimer = null;
    while (queue.length) load(queue.splice(0, MAX_PER_REQUEST));
  }

  function enqueue(rail) {
    queue.push(rail);
    if (!queueTimer) queueTimer = setTimeout(flush, QUEUE_MS);
  }

  function watch(rail) {
    if (!('IntersectionObserver' in window)) {
      enqueue(rail);
      return;
    }
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            enqueue(entry.target);
          });
        },
        // A quarter screen ahead (and behind, for a page opened halfway down): rails below the first screen wait
        // for the first scroll instead of loading with the page.
        { rootMargin: '25% 0px' }
      );
    }
    observer.observe(rail);
  }

  customElements.define(
    'lmb-product-rail',
    class LmbProductRail extends HTMLElement {
      connectedCallback() {
        if (this.dataset.state) return;
        this.dataset.state = 'waiting';
        watch(this);
      }

      disconnectedCallback() {
        if (observer) observer.unobserve(this);
      }

      /* html: the rail section as the Section Rendering API returned it, or null when the request failed. */
      fill(html) {
        const track = this.querySelector('[data-rail-track]');
        if (!track) return;
        const doc = html ? new DOMParser().parseFromString(html, 'text/html') : null;
        const next = doc && doc.querySelector('[data-rail-track]');
        // Only real cards count: if the answer is the shell again (its empty cells are .rail__cell too), keep the
        // fallback rather than leaving grey cells that never fill.
        const real = next && !next.hasAttribute('aria-busy');
        if (!real || !next.querySelector('.rail__cell:not([data-rail-placeholder])')) {
          this.fail(track);
          return;
        }
        // Someone who tabbed onto the shell's "See all" card keeps their place on the real one.
        const hadFocus = track.contains(document.activeElement);
        const scroll = track.scrollLeft;
        track.replaceWith(next);
        next.scrollLeft = scroll;
        this.dataset.state = 'loaded';
        if (hadFocus) {
          const link = next.querySelector('.rail__end-link');
          if (link) link.focus({ preventScroll: true });
        }
      }

      fail(track) {
        this.dataset.state = 'failed';
        track.removeAttribute('aria-busy');
        track.querySelectorAll('[data-rail-placeholder]').forEach((cell) => cell.remove());
      }
    }
  );
})();
